import { FC } from 'react'
import axios from 'axios'
import { FormikHelpers, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { FormItem } from '@/components/atoms/FormItem'
import { SettingButton } from '@/components/atoms/SettingButton'
import { FormValue, MessageType } from '@/types'
import { useStores } from '@/utils'
import { CountrySelectField } from '@/components/atoms/CountrySelectField'
import { TotalCampaignBudgetField } from '@/components/atoms/TotalCampaignBudgetField'
import { InputFieldGroup } from '@/components/molecules/InputFieldGroup'
import { FormGroup } from '@/components/molecules/FormGroup'
import { SourceAqaAdsSelectField } from '@/components/atoms/SourceAqaAdsSelectField'
import styles from './index.module.scss'

export const AdInquiryFormGroup: FC = () => {
  const { messages } = useStores()

  const initialValue: FormValue = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    companyName: '',
    website: '',
    country: '',
    budget: '',
    source: '',
    comment: '',
  }

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().max(50, 'This field must be 50 characters or less').required('This field is required.'),
    lastName: Yup.string().max(50, 'This field must be 50 characters or less').required('This field is required.'),
    phone: Yup.string().max(20, 'This field must be at least 20 characters').required('This field is required.'),
    email: Yup.string().email('Email address format is different.').required('This field is required.'),
    companyName: Yup.string().max(50, 'This field must be 50 characters or less').required('This field is required.'),
    website: Yup.string().required('This field is required.'),
    country: Yup.string().required('This field is required.'),
    budget: Yup.string().required('This field is required.'),
    comment: Yup.string().max(400, 'This field must be at least 400 characters'),
  })

  const formSubmit = async (formValue: FormValue, actions: FormikHelpers<FormValue>) => {
    try {
      await axios.post('api/contact', formValue)
      actions.setSubmitting(false)
      messages.add({
        type: MessageType.Info,
        body: 'Thank you! Your inquiry has been submitted.',
        isTranslated: true,
        ttl: 5000,
      })
      actions.resetForm()
    } catch (error) {
      messages.add({
        type: MessageType.Error,
        body: 'Failed to send your inquiry.',
        isDismissable: true,
        ttl: 5000,
      })
    }
  }

  return (
    <FormGroup withoutBorder>
      <Formik
        initialValues={initialValue}
        validationSchema={validationSchema}
        onSubmit={async (formValue: FormValue, actions: FormikHelpers<FormValue>) => {
          await formSubmit(formValue, actions)
        }}
      >
        {({ values, isSubmitting }) => (
          <Form>
            <FormItem label='First name' isRequired isColumn>
              <Field name='firstName' placeholder='John' fieldType='setting' component={InputFieldGroup} />
            </FormItem>
            <FormItem label='Last name' isRequired isColumn>
              <Field name='lastName' placeholder='Doe' fieldType='setting' component={InputFieldGroup} />
            </FormItem>
            <FormItem label='Phone number' isRequired isColumn>
              <Field
                name='phone'
                type='tel'
                placeholder='555-555-1234'
                fieldType='setting'
                component={InputFieldGroup}
              />
            </FormItem>
            <FormItem label='Company email' isRequired isColumn>
              <Field
                name='email'
                type='email'
                placeholder='john@doe.com'
                fieldType='setting'
                component={InputFieldGroup}
              />
            </FormItem>
            <FormItem label='Comapny name' isRequired isColumn>
              <Field name='companyName' placeholder='AQA' fieldType='setting' component={InputFieldGroup} />
            </FormItem>
            <FormItem label='Website' isRequired isColumn>
              <Field name='website' placeholder='www.aqa3.io' fieldType='setting' component={InputFieldGroup} />
            </FormItem>
            <FormItem label='Country' isRequired isColumn>
              <CountrySelectField country={values?.country} />
            </FormItem>
            <FormItem label='Total campaign budget' isRequired isColumn>
              <TotalCampaignBudgetField />
            </FormItem>
            <FormItem label='Where did you hear about AQA Ads?' isColumn>
              <SourceAqaAdsSelectField source={values?.source} />
            </FormItem>
            <FormItem label='Additional questions/comments' isColumn>
              <Field
                name='comment'
                placeholder='Please provide any additional information to help with your inquiry'
                fieldType='setting'
                component={InputFieldGroup}
              />
            </FormItem>
            <div className={styles.buttonContainer}>
              <SettingButton isLoading={isSubmitting} disabled={isSubmitting} buttonType='submit'>
                Submit
              </SettingButton>
            </div>
          </Form>
        )}
      </Formik>
    </FormGroup>
  )
}
